import { type EventEmitter } from 'events';
import {
  EventType,
  type IssueDetectorResult,
  type IssuePayload,
} from 'webrtc-issue-detector';
import { Subject } from 'rxjs';

export type TrackedIssue = {
  startTime: number;
  endTime: number | null;
  lastUpdated: number;
  duration: string;
} & IssuePayload;

export type IssueSummary = Record<string, TrackedIssue[]>;

export type IssuesObserverPayload = {
  new: TrackedIssue[];
  ongoing: TrackedIssue[];
  ended: TrackedIssue[];
};

export class IssueProcessor {
  private issueSummary: IssueSummary = {};

  private ongoingIssues: Map<string, TrackedIssue> = new Map();

  private lastProcessTime = 0;

  private newIssues: TrackedIssue[] = [];

  private intervalId: number | null = null;

  issues = new Subject<IssuesObserverPayload>();

  constructor(
    private readonly eventEmitter: EventEmitter,
    private readonly bufferInterval: number,
  ) {}

  start(): void {
    this.eventEmitter.on(EventType.Issue, this.handleNewIssues.bind(this));
    this.intervalId = setInterval(
      this.processBuffer.bind(this),
      this.bufferInterval,
    ) as unknown as number;
  }

  /* Issues are stored using type-reason as a key. We do this so we
   * can keep track of ongoing issues and mark them as resolved after
   * some time has passed.
   */
  private getIssueKey(issue: IssuePayload): string {
    return `${issue.type}-${issue.reason}`;
  }

  /**
   * Calculate a human readable duration for the issue
   * @returns human readable duration
   */
  private calculateDuration(startTime: number, endTime?: number): string {
    const duration = (endTime ?? Date.now()) - startTime;
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  private handleNewIssues(issues: IssueDetectorResult): void {
    const currentTime = Date.now();
    for (const issue of issues) {
      const key = this.getIssueKey(issue);
      if (this.ongoingIssues.has(key)) {
        const activeIssue = this.ongoingIssues.get(key)!;
        activeIssue.data = issue.data;
        activeIssue.lastUpdated = currentTime;
        activeIssue.duration = this.calculateDuration(activeIssue.startTime);
      } else {
        const newIssue: TrackedIssue = {
          ...issue,
          startTime: currentTime,
          endTime: null,
          lastUpdated: currentTime,
          duration: '0s',
        };
        this.ongoingIssues.set(key, newIssue);
        this.newIssues.push(newIssue);

        if (!this.issueSummary[key]) {
          this.issueSummary[key] = [];
        }

        this.issueSummary[key]!.push(newIssue);
      }
    }
  }

  /*
   * In order to dedupe issue
   */
  private processBuffer(): void {
    const currentTime = Date.now();
    const endedIssues = [];
    // Check for resolved issues
    for (const [key, issue] of this.ongoingIssues.entries()) {
      // TODO: Define the best timeout to consider an issue resolved
      if (
        currentTime - issue.lastUpdated > this.bufferInterval * 3 ||
        issue.endTime !== null
      ) {
        // Issue is considered resolved
        issue.endTime = issue.endTime ?? currentTime;
        issue.duration = this.calculateDuration(issue.startTime, issue.endTime);
        endedIssues.push(issue);
        this.ongoingIssues.delete(key);
      } else {
        // Update duration for ongoing issues
        issue.duration = this.calculateDuration(issue.startTime);
      }
    }

    // Emit issues
    if (this.newIssues.length > 0 || endedIssues.length > 0) {
      this.issues.next({
        ongoing: [...this.ongoingIssues].map(([_key, value]) => ({ ...value })),
        new: this.newIssues.map((el) => ({ ...el })),
        ended: endedIssues.map((el) => ({ ...el })),
      });
      this.newIssues = [];
    }
    this.lastProcessTime = currentTime;
  }

  getIssueSummary(): IssueSummary {
    return this.issueSummary;
  }

  stop(): void {
    this.eventEmitter.removeAllListeners(EventType.Issue);
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
    }

    for (const [_, issue] of this.ongoingIssues.entries()) {
      issue.endTime = Date.now();
    }
    this.processBuffer();
  }

  reset(): void {
    this.issueSummary = {};
    this.ongoingIssues = new Map();
    this.lastProcessTime = 0;
    this.newIssues = [];
    this.intervalId = null;
  }
}

export function createIssueProcessor(
  eventEmitter: EventEmitter,
  bufferInterval: number,
): IssueProcessor {
  return new IssueProcessor(eventEmitter, bufferInterval);
}
